import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { BrainTemplatePicker } from '../components/demo/BrainTemplatePicker';
import { SaveBrainButton } from '../components/demo/SaveBrainButton';
import { SavedBrains } from '../components/demo/SavedBrains';
import { MyRepStats } from '../components/demo/MyRepStats';
import { 
  Phone, Loader2, LogOut, Copy, Check, Play, Pause,
  Volume2, Globe, Sparkles, Building2, ArrowRight,
  PhoneCall, User, Mic, Clock, Music, Sliders, Brain, Pencil
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const responseSpeedLabel = (v) => (v < 0.4 ? 'Relaxed (~2-3s pause)' : v < 0.75 ? 'Natural' : 'Instant');

export default function SalesRepDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Sales Demo Builder states
  const [salesDemoStep, setSalesDemoStep] = useState(1);
  const [salesDemoCompanyName, setSalesDemoCompanyName] = useState('');
  const [salesDemoWebsite, setSalesDemoWebsite] = useState('');
  const [salesDemoData, setSalesDemoData] = useState(null);
  const [salesDemoGenerating, setSalesDemoGenerating] = useState(false);
  const [salesDemoAgentId, setSalesDemoAgentId] = useState(null);
  const [salesDemoConnecting, setSalesDemoConnecting] = useState(false);
  const [salesDemoConnected, setSalesDemoConnected] = useState(false);
  const [customerTransferNumber, setCustomerTransferNumber] = useState('');
  const [savingTransferNumber, setSavingTransferNumber] = useState(false);
  const [copiedNumber, setCopiedNumber] = useState(false);
  
  // Voice configuration
  const [selectedVoice, setSelectedVoice] = useState('11labs-Lily');
  const [selectedAccent, setSelectedAccent] = useState('All');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedBackground, setSelectedBackground] = useState('office');
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);
  const [voiceEmotion, setVoiceEmotion] = useState(1.0);
  const [voiceSearch, setVoiceSearch] = useState('');
  
  // Advanced settings
  const [responsiveness, setResponsiveness] = useState(0.5);
  const [interruptionSensitivity, setInterruptionSensitivity] = useState(0.6);
  
  // Editable prompt and greeting
  const [editableGreeting, setEditableGreeting] = useState('');
  const [editablePrompt, setEditablePrompt] = useState('');
  const [brainId, setBrainId] = useState(null);

  const getBrainPayload = () => ({
    business_name: salesDemoCompanyName,
    website: salesDemoWebsite,
    greeting: editableGreeting,
    prompt: editablePrompt,
    business_data: salesDemoData || {},
    settings: {
      voice_id: selectedVoice, background: selectedBackground, language: selectedLanguage,
      voice_speed: voiceSpeed, voice_temperature: voiceEmotion,
      responsiveness, interruption_sensitivity: interruptionSensitivity,
    },
  });

  const loadBrain = (b) => {
    const st = b.settings || {};
    setBrainId(b.id);
    setSalesDemoCompanyName(b.business_name);
    setSalesDemoWebsite(b.website || '');
    setSalesDemoData({ ...b.business_data, business_name: b.business_name, greeting: b.greeting, personality: b.prompt });
    setEditableGreeting(b.greeting);
    setEditablePrompt(b.prompt);
    if (st.voice_id) setSelectedVoice(st.voice_id);
    if (st.background) setSelectedBackground(st.background);
    if (st.language) setSelectedLanguage(st.language);
    if (st.voice_speed != null) setVoiceSpeed(st.voice_speed);
    if (st.voice_temperature != null) setVoiceEmotion(st.voice_temperature);
    if (st.responsiveness != null) setResponsiveness(st.responsiveness);
    if (st.interruption_sensitivity != null) setInterruptionSensitivity(st.interruption_sensitivity);
    setSalesDemoStep(2);
    toast.success(`Loaded brain for ${b.business_name}`);
  };
  
  // Voice data from API
  const [allVoices, setAllVoices] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [allBackgrounds, setAllBackgrounds] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(null);
  const audioRef = useRef(null);

  // Live demo phone number
  const LIVE_DEMO_NUMBER = '+19377444837';
  const LIVE_DEMO_NUMBER_DISPLAY = '+1 (937) 744-4837';

  // Background sound options
  const backgroundOptions = [
    { value: 'office', label: 'Office', description: 'Professional office ambiance' },
    { value: 'call-center', label: 'Call Center', description: 'Busy call center background' },
    { value: 'coffee-shop', label: 'Coffee Shop', description: 'Casual cafe atmosphere' },
    { value: 'convention-hall', label: 'Convention Hall', description: 'Large venue ambiance' },
    { value: 'summer-outdoor', label: 'Summer Outdoor', description: 'Outdoor nature sounds' },
    { value: 'mountain-outdoor', label: 'Mountain Outdoor', description: 'Mountain ambiance' },
    { value: 'static-noise', label: 'Static Noise', description: 'White noise' },
    { value: 'null', label: 'None', description: 'No background sound' },
  ];

  useEffect(() => {
    if (!user || (user.role !== 'sales_rep' && user.role !== 'admin')) {
      toast.error('Access denied. Sales rep or admin access required.');
      navigate('/login');
      return;
    }
    fetchVoices();
    setLoading(false);
  }, [user, navigate]);

  const fetchVoices = async () => {
    setVoicesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/demo/voices`);
      if (res.ok) {
        const data = await res.json();
        setAllVoices(data.voices || []);
        setAllLanguages(data.languages || []);
        setAllBackgrounds(data.backgrounds || []);
      }
    } catch (err) {
      console.error('Failed to fetch voices:', err);
    } finally {
      setVoicesLoading(false);
    }
  };

  // Get unique accents from voices
  const uniqueAccents = ['All', ...new Set(allVoices.map(v => v.accent).filter(Boolean))];
  
  // Filter voices based on selections
  const filteredVoices = allVoices.filter(v => {
    const matchesAccent = selectedAccent === 'All' || v.accent === selectedAccent;
    const matchesGender = selectedGender === 'all' || v.gender === selectedGender;
    const matchesSearch = !voiceSearch || 
      v.voice_name?.toLowerCase().includes(voiceSearch.toLowerCase()) ||
      v.name?.toLowerCase().includes(voiceSearch.toLowerCase()) ||
      v.provider?.toLowerCase().includes(voiceSearch.toLowerCase());
    return matchesAccent && matchesGender && matchesSearch;
  });

  // Play voice preview
  const playVoicePreview = (voice) => {
    if (playingPreview === voice.voice_id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingPreview(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (voice.preview_url) {
        audioRef.current = new Audio(voice.preview_url);
        audioRef.current.play();
        audioRef.current.onended = () => setPlayingPreview(null);
        setPlayingPreview(voice.voice_id);
      }
    }
  };

  // Generate Pro Demo - Step 1: Analyze business, Step 2: Create agent
  const handleGenerateDemo = async () => {
    setBrainId(null);
    if (!salesDemoCompanyName.trim()) {
      toast.error('Please enter a company name');
      return;
    }
    
    setSalesDemoGenerating(true);
    try {
      // Step 1: Analyze business to generate prompt/greeting
      const analyzeRes = await fetch(`${API_URL}/api/demo/analyze-business`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: salesDemoCompanyName.trim(),
          website_url: salesDemoWebsite.trim() || null,
          google_url: null
        })
      });
      
      if (!analyzeRes.ok) {
        const err = await analyzeRes.json();
        toast.error(err.detail || 'Failed to analyze business');
        setSalesDemoGenerating(false);
        return;
      }
      
      const demoData = await analyzeRes.json();
      setSalesDemoData(demoData);
      setEditableGreeting(demoData.greeting || '');
      setEditablePrompt(demoData.personality || '');
      setSalesDemoStep(2);
      toast.success('Pro Demo ready! Review and connect.');
    } catch (err) {
      console.error('Generate demo error:', err);
      toast.error('Failed to generate demo');
    } finally {
      setSalesDemoGenerating(false);
    }
  };

  // Create agent and connect to phone - Step 2
  const handleConnectDemoNumber = async () => {
    if (!salesDemoData) {
      toast.error('No demo data');
      return;
    }
    
    setSalesDemoConnecting(true);
    try {
      // Create the actual Retell agent
      const createRes = await fetch(`${API_URL}/api/demo/create-temp-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_data: {
            business_name: salesDemoData.business_name || salesDemoCompanyName,
            category: salesDemoData.category || 'general',
            greeting: editableGreeting || salesDemoData.greeting,
            personality: editablePrompt || salesDemoData.personality,
            services: salesDemoData.services || [],
            hours: salesDemoData.hours || '',
            phone: salesDemoData.phone || '',
            address: salesDemoData.address || ''
          },
          voice_id: selectedVoice,
          background_noise: selectedBackground === 'null' ? 'none' : selectedBackground,
          voice_speed: voiceSpeed,
          voice_temperature: voiceEmotion,
          responsiveness,
          interruption_sensitivity: interruptionSensitivity,
          language: selectedLanguage,
          custom_greeting: editableGreeting || salesDemoData.greeting,
          custom_prompt: editablePrompt || salesDemoData.personality
        })
      });
      
      if (!createRes.ok) {
        const err = await createRes.json();
        toast.error(err.detail || 'Failed to create agent');
        setSalesDemoConnecting(false);
        return;
      }
      
      const agentData = await createRes.json();
      const agentId = agentData.agent_id;
      setSalesDemoAgentId(agentId);
      
      // Connect the live phone number to this agent
      const connectRes = await fetch(`${API_URL}/api/admin/connect-demo-number`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_id: agentId,
          business_name: salesDemoCompanyName
        })
      });
      
      if (connectRes.ok) {
        setSalesDemoConnected(true);
        setSalesDemoStep(3);
        toast.success('Phone connected! Ready to call.');
      } else {
        const err = await connectRes.json();
        toast.error(err.detail || 'Failed to connect phone');
      }
    } catch (err) {
      console.error('Connect demo error:', err);
      toast.error('Failed to connect demo');
    } finally {
      setSalesDemoConnecting(false);
    }
  };

  // Set transfer number
  const handleSetTransferNumber = async () => {
    if (!customerTransferNumber.trim()) {
      toast.error('Please enter a transfer number');
      return;
    }
    
    setSavingTransferNumber(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/set-transfer-number`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_id: salesDemoAgentId,
          transfer_number: customerTransferNumber
        })
      });
      
      if (res.ok) {
        toast.success('Transfer number set!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to set transfer number');
      }
    } catch (err) {
      toast.error('Failed to set transfer number');
    } finally {
      setSavingTransferNumber(false);
    }
  };

  // Copy phone number
  const handleCopyNumber = () => {
    navigator.clipboard.writeText(LIVE_DEMO_NUMBER);
    setCopiedNumber(true);
    setTimeout(() => setCopiedNumber(false), 2000);
    toast.success('Number copied!');
  };

  // Reset demo
  const resetDemo = () => {
    setSalesDemoStep(1);
    setSalesDemoCompanyName('');
    setSalesDemoWebsite('');
    setSalesDemoData(null);
    setSalesDemoAgentId(null);
    setSalesDemoConnected(false);
    setCustomerTransferNumber('');
    setEditableGreeting('');
    setEditablePrompt('');
    setBrainId(null);
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="sales-rep-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-semibold text-slate-900">Front Desk Pro</span>
                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Sales Demo</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                Welcome, <span className="font-medium text-slate-900">{user?.name || user?.email}</span>
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="text-slate-600 hover:text-red-600 hover:border-red-200"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Live Demo Number Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <PhoneCall className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Your Live Demo Number</p>
                <p className="text-2xl font-bold">{LIVE_DEMO_NUMBER_DISPLAY}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleCopyNumber} className="bg-white/20 hover:bg-white/30 text-white border-0">
                {copiedNumber ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copiedNumber ? 'Copied!' : 'Copy'}
              </Button>
              <Button onClick={() => window.open(`tel:${LIVE_DEMO_NUMBER}`, '_self')} className="bg-white text-blue-600 hover:bg-blue-50">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-6">
          {[
            { num: 1, label: 'Business Info' },
            { num: 2, label: 'Customize Voice' },
            { num: 3, label: 'Connect & Call' }
          ].map((step, i) => (
            <React.Fragment key={step.num}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  salesDemoStep >= step.num ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step.num}
                </div>
                <span className={`text-sm font-medium ${salesDemoStep >= step.num ? 'text-slate-900' : 'text-slate-400'}`}>
                  {step.label}
                </span>
              </div>
              {i < 2 && <div className={`w-12 h-0.5 ${salesDemoStep > step.num ? 'bg-blue-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Business Info + Voice Selection */}
        {salesDemoStep === 1 && (
          <div className="space-y-6 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
            <MyRepStats token={token} />

            <SavedBrains token={token} onLoad={loadBrain} />

            {/* Business Information */}
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Business Information</h2>
                  <p className="text-sm text-slate-500">Enter the prospect's details</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Business Name *</label>
                  <Input
                    value={salesDemoCompanyName}
                    onChange={(e) => setSalesDemoCompanyName(e.target.value)}
                    placeholder="e.g., Smith Plumbing Services"
                    className="h-11 bg-white border-slate-300 text-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Website URL <span className="text-slate-400">(optional)</span></label>
                  <Input
                    value={salesDemoWebsite}
                    onChange={(e) => setSalesDemoWebsite(e.target.value)}
                    placeholder="e.g., smithplumbing.com"
                    className="h-11 bg-white border-slate-300 text-slate-900"
                  />
                  <p className="text-xs text-slate-500 mt-1">Pro will learn from the website</p>
                </div>
              </div>
            </Card>

            {/* Voice Selection */}
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Voice Selection</h2>
                  <p className="text-sm text-slate-500">Choose your Pro's voice</p>
                </div>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Gender</label>
                  <Select value={selectedGender} onValueChange={setSelectedGender}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Accent</label>
                  <Select value={selectedAccent} onValueChange={setSelectedAccent}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueAccents.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Language</label>
                  <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allLanguages.map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Search */}
              <Input
                placeholder="Search voices..."
                value={voiceSearch}
                onChange={(e) => setVoiceSearch(e.target.value)}
                className="mb-3 bg-white border-slate-300 text-slate-900"
              />

              {/* Voice Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                {filteredVoices.slice(0, 30).map(voice => (
                  <div
                    key={voice.voice_id}
                    onClick={() => setSelectedVoice(voice.voice_id)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedVoice === voice.voice_id
                        ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                        : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm truncate">{voice.voice_name || voice.name}</span>
                      {voice.preview_url && (
                        <button
                          onClick={(e) => { e.stopPropagation(); playVoicePreview(voice); }}
                          className={`p-1.5 rounded-full ${selectedVoice === voice.voice_id ? 'bg-white/20 hover:bg-white/30' : 'bg-slate-200 hover:bg-slate-300'}`}
                        >
                          {playingPreview === voice.voice_id ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                    <p className={`text-xs mt-1 ${selectedVoice === voice.voice_id ? 'text-blue-100' : 'text-slate-500'}`}>
                      {voice.gender} • {voice.accent || 'Neutral'}
                    </p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Voice Settings */}
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Sliders className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Voice Settings</h2>
                  <p className="text-sm text-slate-500">Adjust speed, emotion and response timing</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">Voice Speed</label>
                    <span className="text-sm text-blue-600 font-medium">{voiceSpeed.toFixed(1)}x</span>
                  </div>
                  <Slider
                    value={[voiceSpeed]}
                    onValueChange={([v]) => setVoiceSpeed(v)}
                    min={0.5}
                    max={1.5}
                    step={0.1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Slower</span>
                    <span>Faster</span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">Voice Emotion</label>
                    <span className="text-sm text-blue-600 font-medium">{voiceEmotion.toFixed(1)}</span>
                  </div>
                  <Slider
                    value={[voiceEmotion]}
                    onValueChange={([v]) => setVoiceEmotion(v)}
                    min={0.5}
                    max={1.5}
                    step={0.1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Calm</span>
                    <span>Expressive</span>
                  </div>
                </div>
                <div data-testid="response-speed-control">
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">Response Speed</label>
                    <span className="text-sm text-blue-600 font-medium" data-testid="response-speed-value">{(responsiveness * 100).toFixed(0)}% · {responseSpeedLabel(responsiveness)}</span>
                  </div>
                  <Slider
                    value={[responsiveness]}
                    onValueChange={([v]) => setResponsiveness(v)}
                    min={0.1}
                    max={1}
                    step={0.05}
                    className="w-full"
                    data-testid="response-speed-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Natural pause</span>
                    <span>Instant reply</span>
                  </div>
                </div>
                <div data-testid="interruption-control">
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">Interruption Sensitivity</label>
                    <span className="text-sm text-blue-600 font-medium">{(interruptionSensitivity * 100).toFixed(0)}%</span>
                  </div>
                  <Slider
                    value={[interruptionSensitivity]}
                    onValueChange={([v]) => setInterruptionSensitivity(v)}
                    min={0.1}
                    max={1}
                    step={0.05}
                    className="w-full"
                    data-testid="interruption-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>Lets caller finish</span>
                    <span>Easy to interrupt</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Background Sound */}
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Music className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Background Sound</h2>
                  <p className="text-sm text-slate-500">Add ambient noise</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {backgroundOptions.map(bg => (
                  <button
                    key={bg.value}
                    onClick={() => setSelectedBackground(bg.value)}
                    className={`p-3 rounded-lg text-left transition-all ${
                      selectedBackground === bg.value
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    <p className="font-medium text-sm">{bg.label}</p>
                    <p className={`text-xs mt-0.5 ${selectedBackground === bg.value ? 'text-orange-100' : 'text-slate-500'}`}>
                      {bg.description}
                    </p>
                  </button>
                ))}
              </div>
            </Card>

            {salesDemoData && (
              <Button
                onClick={() => setSalesDemoStep(2)}
                className="w-full h-12 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="sales-back-to-review-btn"
              >
                <ArrowRight className="w-4 h-4 mr-2" />Back to Review (keep my edits)
              </Button>
            )}

            {/* Generate Button */}
            <Button 
              onClick={handleGenerateDemo}
              disabled={salesDemoGenerating || !salesDemoCompanyName.trim()}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg"
            >
              {salesDemoGenerating ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating Pro Demo...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" />Generate Pro Demo</>
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Review & Connect */}
        {salesDemoStep === 2 && salesDemoData && (
          <div className="space-y-6">
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Mic className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Pro Demo Ready</h2>
                    <p className="text-sm text-slate-500">Review before connecting</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => setSalesDemoStep(1)} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="sales-edit-settings-btn">
                    <Pencil className="w-4 h-4 mr-1" />Edit Settings
                  </Button>
                  <Button size="sm" onClick={resetDemo} className="bg-white border border-slate-300 text-slate-500 hover:bg-slate-50" data-testid="sales-start-over-btn">Start Over</Button>
                </div>
              </div>

              {/* Greeting */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Pro Greeting</label>
                <Textarea
                  value={editableGreeting}
                  onChange={(e) => setEditableGreeting(e.target.value)}
                  className="min-h-[80px] bg-white border-slate-300 text-slate-900"
                  data-testid="sales-greeting-textarea"
                />
              </div>

              {/* Build the Brain */}
              <div className="mb-4 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50 p-5" data-testid="sales-build-brain-section">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-slate-900">Build the Brain</h3>
                    <p className="text-xs text-slate-500">Everything your Pro knows and how it behaves. Add prices, services, policies, then connect.</p>
                  </div>
                  <SaveBrainButton token={token} getPayload={getBrainPayload} brainId={brainId} onSaved={(b) => setBrainId(b.id)} />
                </div>
                <BrainTemplatePicker
                  businessName={salesDemoCompanyName}
                  prompt={editablePrompt}
                  greeting={editableGreeting}
                  onApply={({ prompt, greeting }) => { setEditablePrompt(prompt); setEditableGreeting(greeting); }}
                />
                <Textarea
                  value={editablePrompt}
                  onChange={(e) => setEditablePrompt(e.target.value)}
                  className="min-h-[320px] bg-white border-slate-300 text-slate-900 font-mono text-sm leading-relaxed"
                  placeholder="Describe your Pro's personality, services, pricing and rules..."
                  data-testid="sales-brain-prompt-textarea"
                />
                <p className="text-xs text-slate-400 mt-2">{editablePrompt.length} characters</p>
              </div>

              {/* Info */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <label className="block text-xs text-slate-500 mb-1">Business</label>
                  <p className="font-medium text-slate-900 text-sm">{salesDemoCompanyName}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <label className="block text-xs text-slate-500 mb-1">Voice</label>
                  <p className="font-medium text-slate-900 text-sm">
                    {allVoices.find(v => v.voice_id === selectedVoice)?.voice_name || selectedVoice}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <label className="block text-xs text-slate-500 mb-1">Background</label>
                  <p className="font-medium text-slate-900 text-sm">
                    {backgroundOptions.find(b => b.value === selectedBackground)?.label || 'None'}
                  </p>
                </div>
              </div>

              <Button 
                onClick={handleConnectDemoNumber}
                disabled={salesDemoConnecting}
                className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
              >
                {salesDemoConnecting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
                ) : (
                  <><Phone className="w-4 h-4 mr-2" />Connect to Demo Phone</>
                )}
              </Button>
            </Card>
          </div>
        )}

        {/* Step 3: Connected */}
        {salesDemoStep === 3 && (
          <div className="space-y-6">
            <Card className="p-6 bg-white border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Demo Connected!</h2>
                    <p className="text-sm text-slate-500">Ready for {salesDemoCompanyName}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={resetDemo}>New Demo</Button>
              </div>

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <p className="text-green-800 font-medium">Call {LIVE_DEMO_NUMBER_DISPLAY} to hear your Pro</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Transfer Number (optional)</label>
                <p className="text-xs text-slate-500 mb-2">When caller asks for a human, Pro transfers here</p>
                <div className="flex gap-2">
                  <Input
                    value={customerTransferNumber}
                    onChange={(e) => setCustomerTransferNumber(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="flex-1 h-11 bg-white border-slate-300 text-slate-900"
                  />
                  <Button onClick={handleSetTransferNumber} disabled={savingTransferNumber || !customerTransferNumber.trim()}>
                    {savingTransferNumber ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set'}
                  </Button>
                </div>
              </div>

              <Button 
                onClick={() => window.open(`tel:${LIVE_DEMO_NUMBER}`, '_self')}
                className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Demo Number
              </Button>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
